import React, { useMemo } from "react"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { ProductConfiguratorOptionProps } from "shared/types/ProductConfigurator"
import { ConditionalButton } from "shared/ui/buttons/ConditionalButton"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { SwipeToDelete } from "shared/ui/swipe-to-delete/SwipeToDelete"

import "../styles/_product-configurator-option.scss"
import { ProductConfiguratorOptionIcon } from "./ProductConfiguratorOptionIcon"
import { ProductConfiguratorOptionImage } from "./ProductConfiguratorOptionImage"
import { ProductConfiguratorOptionText } from "./ProductConfiguratorOptionText"

export interface ProductConfiguratorOptionRendererProps
  extends ProductConfiguratorOptionProps {
  canSwipeToDelete?: boolean
}

function ProductConfiguratorOptionRendererComponent({
  option,
  payload,
  isSelected,
  isExpandable,
  isExpanded,
  isDisabled,
  canSwipeToDelete = true,
  onSelect,
  onExpand,
  containerProps
}: ProductConfiguratorOptionRendererProps) {
  const resolvedClassName = classNames("product-configurator-option", {
    selected: isSelected,
    expandable: isExpandable,
    expanded: isExpanded,
    disabled: isDisabled
  })

  const hasSecondaryAction = false
  // !!onSelect && (!isSelected || !payload?.swipeToDelete) && !!onExpand

  const onPrimary = useMemo(() => {
    if (!isSelected) {
      return onSelect
    }
    return onExpand
  }, [onSelect, isSelected])

  const icon = useMemo(() => {
    if (isExpandable) {
      return IconName.SMALL_CARET_UP
    }
    return payload?.icon
  }, [isExpandable, payload?.icon])

  const iconSize = useMemo(() => {
    if (isExpandable) {
      return IconSize.XXS
    }
    return IconSize.MEDIUM
  }, [isExpandable, payload?.icon])

  const canDeleteByClick =
    payload?.canDelete && !payload?.swipeToDelete && isSelected

  const primaryActionButtonID = option.payload.id
    ? `${option.payload.id}__primary-action`
    : undefined
  const secondaryActionButtonID = option.payload.id
    ? `${option.payload.id}__secondary-action`
    : undefined

  if (option.payload.isHidden) return null

  const content = (
    <ConditionalButton
      id={primaryActionButtonID}
      onClick={hasSecondaryAction ? undefined : onPrimary}
      disabled={hasSecondaryAction || isDisabled}
      isButton={!hasSecondaryAction && !!onPrimary}
      className="product-configurator-option__content"
    >
      <ConditionalButton
        id={primaryActionButtonID}
        onClick={hasSecondaryAction ? onSelect : undefined}
        disabled={!hasSecondaryAction || isDisabled}
        isButton={hasSecondaryAction}
        className="product-configurator-option__information"
      >
        <ProductConfiguratorOptionImage
          image={option.payload.image}
          canDelete={canDeleteByClick}
          isDisabled={isDisabled}
        />
        <ProductConfiguratorOptionText
          title={option.payload.title || option.title}
          description={option.payload.description}
        />
      </ConditionalButton>
      <ProductConfiguratorOptionIcon
        id={secondaryActionButtonID}
        icon={icon}
        iconSize={iconSize}
        // When the option is the button of an accordion, this caret is its
        // chevron, and the accordion styles hide or rotate it from here.
        iconClassName={isExpandable ? "accordion-button__indicator" : undefined}
        onClick={hasSecondaryAction && !isDisabled ? onExpand : undefined}
      />
    </ConditionalButton>
  )

  return (
    <div
      className={classNames(
        containerProps?.className,
        "product-configurator-option-container"
      )}
    >
      {canSwipeToDelete ? (
        <SwipeToDelete
          className={resolvedClassName}
          containerClassName="product-configurator-option__swipe-to-delete"
          isDisabled={!payload?.swipeToDelete || !isSelected || isDisabled}
          onDelete={onSelect}
        >
          {content}
        </SwipeToDelete>
      ) : (
        <div className={resolvedClassName}>{content}</div>
      )}
    </div>
  )
}

export const ProductConfiguratorOptionRenderer = React.memo(
  ProductConfiguratorOptionRendererComponent
) as typeof ProductConfiguratorOptionRendererComponent
